import debounce from "lodash/debounce";

/**
 * El POS no calcula impuestos ni totales de una orden de compra: manda lo capturado a
 * POST purchase-orders/calculate y pinta lo que regresa el back. El back usa ese mismo calculo al
 * guardar, recibir y registrar el gasto, asi que pantalla, BD, recepcion y gasto cuadran.
 */

export interface CalculationItem {
  key: string;
  id: number | null;
  product_id: number;
  quantity: number;
  price: number;
  discount: number;
  iva_id: number | null;
  ieps_id: number | null;
}

export interface CalculatedLine extends CalculationItem {
  tax_rate: number;
  /** Impuesto por unidad, como la columna purchase_orders_details.tax. */
  tax: number;
  /** Cantidad x precio, sin impuestos. */
  total: number;
  tax_amount: number;
  iva: number;
  ieps: number;
  grand_total: number;
}

export interface PurchaseOrderCalculation {
  items: CalculatedLine[];
  subtotal: number;
  iva: number;
  ieps: number;
  tax: number;
  discount: number;
  grand_total: number;
}

export interface CalculationPayload {
  purchase_order_id?: number | null;
  items: CalculationItem[];
}

export const EMPTY_CALCULATION: PurchaseOrderCalculation = {
  items: [],
  subtotal: 0,
  iva: 0,
  ieps: 0,
  tax: 0,
  discount: 0,
  grand_total: 0,
};

/** Renglon tal como lo tienen las pantallas: una partida guardada o un producto por pedir. */
export interface CapturedRow {
  id?: number | string | null;
  product_id?: number | string | null;
  product?: { id: number } | null;
  quantity: number | string;
  price: number | string;
  discount?: number | string | null;
  iva_id?: number | null;
  ieps_id?: number | null;
}

const rowKey = (row: CapturedRow): string => String(row.id ?? row.product?.id ?? row.product_id);

/** Solo lo capturado: tasas y totales no viajan porque el back no los cree. */
export const toCalculationItems = (rows: CapturedRow[]): CalculationItem[] =>
  rows.map((row) => ({
    key: rowKey(row),
    id: row.id !== undefined && row.id !== null && Number.isInteger(Number(row.id)) ? Number(row.id) : null,
    product_id: Number(row.product?.id ?? row.product_id),
    quantity: Number(row.quantity) || 0,
    price: Number(row.price) || 0,
    discount: Number(row.discount) || 0,
    iva_id: row.iva_id ?? null,
    ieps_id: row.ieps_id ?? null,
  }));

export const lineFor = (calculation: PurchaseOrderCalculation, row: CapturedRow): CalculatedLine | undefined =>
  calculation.items.find((line) => String(line.key) === rowKey(row));

export interface CalculationSchedulerOptions {
  calculate: (payload: CalculationPayload) => Promise<{ success: boolean; object?: PurchaseOrderCalculation } | undefined>;
  onResult: (calculation: PurchaseOrderCalculation) => void;
  onError?: (error: unknown) => void;
  onBusyChange?: (busy: boolean) => void;
  wait?: number;
}

/**
 * Agrupa las capturas rapidas en una sola peticion y descarta respuestas viejas: si el usuario
 * cambia la cantidad dos veces, se pinta el calculo de la ultima, no el que llegue al final.
 */
export const createCalculationScheduler = ({
  calculate,
  onResult,
  onError,
  onBusyChange,
  wait = 300,
}: CalculationSchedulerOptions) => {
  let sequence = 0;
  let scheduled = false;

  const run = async (payload: CalculationPayload) => {
    scheduled = false;
    const current = ++sequence;
    try {
      const response = await calculate(payload);
      if (current !== sequence) return; // ya salio una peticion mas nueva
      if (response?.success && response.object) {
        onResult(response.object);
      } else {
        onError?.(response);
      }
    } catch (error) {
      if (current !== sequence) return;
      onError?.(error);
    }
    onBusyChange?.(scheduled);
  };

  const debounced = debounce(run, wait);

  return {
    schedule: (payload: CalculationPayload) => {
      scheduled = true;
      onBusyChange?.(true);
      debounced(payload);
    },
    cancel: () => {
      debounced.cancel();
      sequence++; // una respuesta que venga en camino ya no aplica
      scheduled = false;
      onBusyChange?.(false);
    },
  };
};
